import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, api } from "../lib/api";
import { useSession } from "../lib/session";

/**
 * Where this account is signed in, and how one of those places is stopped
 * (ADR 000086).
 *
 * The screen exists for a machine somebody no longer controls. A session lasts
 * thirty days and is absolute rather than sliding, so before this the answer to
 * a laptop left in an edit suite was to wait a month.
 *
 * The rows say when and not where, because nothing records where: see the ADR
 * for why that is a cost taken deliberately. It is also why the loud button is
 * the one that does not need to know which row is which.
 */
export function SessionsRoute() {
  const queryClient = useQueryClient();
  const { signOut } = useSession();
  const [failure, setFailure] = useState<string | null>(null);

  const sessions = useQuery({ queryKey: ["sessions"], queryFn: () => api.listSessions() });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["sessions"] });
  };

  const end = useMutation({
    mutationFn: (id: string) => api.endSession(id as never),
    onSuccess: refresh,
    onError: (error) => setFailure(describe(error)),
  });

  const endOthers = useMutation({
    mutationFn: () => api.endOtherSessions(),
    onSuccess: refresh,
    onError: (error) => setFailure(describe(error)),
  });

  const elsewhere = (sessions.data ?? []).filter((session) => !session.current).length;
  const busy = end.isPending || endOthers.isPending;

  return (
    <section>
      <header className="page-head">
        <h1>Sessions</h1>
        <p className="muted">
          Every browser this account is signed in on. Ending one refuses its next request, whatever
          it was in the middle of. It does not touch your{" "}
          <Link to="/settings/connectors">connectors</Link> or your{" "}
          <Link to="/settings/keys">agent keys</Link>, which are their own credentials and are
          stopped on their own pages.
        </p>
      </header>

      {failure && (
        <p role="alert" className="error">
          {failure}
        </p>
      )}

      {sessions.isPending ? (
        <p role="status">Loading…</p>
      ) : sessions.error ? (
        <p className="error">{describe(sessions.error)}</p>
      ) : (
        <>
          <ul className="keys">
            {sessions.data.map((session) => (
              <li key={session.id}>
                <span className="key-body">
                  <strong>
                    Signed in {when(session.createdAt)}
                    {session.current && <span className="badge">this browser</span>}
                  </strong>
                  <span className="key-meta">
                    expires {shortDate(session.expiresAt)}
                    {" · "}
                    {session.id}
                  </span>
                </span>
                <button
                  type="button"
                  className="ghost danger"
                  disabled={busy}
                  onClick={() => {
                    // Ending the session you are sitting in is signing out, so
                    // it goes through sign-out rather than this route: that is
                    // what clears the cookie and drops the cached queries,
                    // which deleting the record alone would leave behind.
                    if (session.current) {
                      void signOut();
                      return;
                    }
                    if (window.confirm("End this session? Whoever is using it is signed out.")) {
                      end.mutate(session.id);
                    }
                  }}
                >
                  {session.current ? "Sign out" : "End"}
                </button>
              </li>
            ))}
          </ul>

          <p className="danger-zone">
            <button
              type="button"
              className="ghost danger"
              disabled={busy || elsewhere === 0}
              onClick={() => {
                if (
                  window.confirm(
                    "Sign out everywhere else? You stay signed in here, and every other browser is signed out.",
                  )
                ) {
                  endOthers.mutate();
                }
              }}
            >
              Sign out everywhere else
            </button>
          </p>
          <p className="muted">
            {elsewhere === 0
              ? "This is the only browser you are signed in on."
              : "Ends the other sessions and leaves this one alone. Use it when you cannot tell which row is the machine you have lost."}
          </p>
        </>
      )}
    </section>
  );
}

/**
 * When a session began, to the minute.
 *
 * The minute rather than the day because the rows carry nothing else to tell
 * them apart: nothing records where a session was made (ADR 000086), so two
 * sign-ins on one afternoon are otherwise the same row twice.
 */
function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function describe(error: unknown): string {
  if (error instanceof ApiError) return error.userMessage;
  return error instanceof Error ? error.message : "Something went wrong.";
}
